import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Grid,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Checkbox
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { API_SLUGS, HELPER_TEXTS, ROUTES, STRINGS } from "../../constants";
import { axiosInstance } from "../../services/axios";
import { IAlertState, useAlertState } from "../../store/alertState";
import { debounce, filter, isEmpty, lowerCase } from "lodash";


interface IAddVariableFormValues {
  name: string;
  data: any;
}

interface IAddForm extends RouteComponentProps {
  updateOnly?: boolean;
  defaultFormValues?: any;
  updateUUID?: string;
  callback?: () => void;
  preventRedirect?: boolean;
}

const AddVariableForm: FC<IAddForm> = ({
  history,
  match,
  preventRedirect = false,
  callback = undefined,
}) => {
  const [formValues, setFormValues] = useState({
    name: "",
    data: {},
  } as IAddVariableFormValues);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(STRINGS.EMPTY);
  const [successMessage, setSuccessMessage] = useState(STRINGS.EMPTY);
  const [hideSave, setHideSave] = useState(false);
  const [groups, setGroups] = useState<any[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<any>();
  const [isGlobal, setIsGlobal] =  useState<boolean>(false)
  const { setMessage } = useAlertState() as IAlertState;

  useEffect(() => {
    fetchGroups()
  }, []);

  const handleChange = async (value: any, type: "name" | "data") => {
    let newValues = { ...formValues, [type]: value };

    setFormValues(newValues);
  };
  const handleSubmit = async () => {
    if (!isGlobal) {
      if (formValues?.name.trim() != "") {
        await createGroupPattern(formValues?.name.trim());
      } else {
        setErrorMessage("Please fill all the mandatory fields");
      }
    }
    else {
      if (formValues?.name.trim() != "") {
        await createGlobalPattern(
          formValues?.name.trim()
        );
      } else {
        setErrorMessage("Please fill all the mandatory fields");
      }
    }
  };

  const createGlobalPattern = async (name: string) => {
    try {
      let variableResponse = await axiosInstance.post(
        API_SLUGS.POST_VARIABLES,
        { name: name, data: { variables: [] } }
      );
      if (!preventRedirect) {
        history.push(ROUTES.GENERATOR_SETS);
      }
      if (callback) {
        callback();
      }
      setMessage({ title: "Pattern Created Successfully", type: "success" })
    } catch (error: any) {
      try {
        setErrorMessage(error.response?.data?.message);
      } catch (error: any) { }
    }
  };

  const createGroupPattern = async (name: string) => {
    try {
      let variableResponse = await axiosInstance.post(
        API_SLUGS.PATTERNS,
        { name: name, data: { variables: [] }, groupId: selectedGroup.id }
      );
      if (!preventRedirect) {
        history.push(ROUTES.GENERATOR_SETS);
      }
      if (callback) {
        callback();
      }
      setMessage({ title: "Pattern Created Successfully", type: "success" })
    } catch (error: any) {
      try {
        setErrorMessage(error.response?.data?.message);
      } catch (error: any) { }
    }
  };


  const fetchGroups = async (searchQuery?: string | null) => {
    try {
      const response = await axiosInstance.get(
        `/group?limit=50${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`
      );
      setGroups(response.data?.groups || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setGroups([]);
    }
  };

  const debouncedFetchGroups = debounce(fetchGroups, 300);

  const handleGroupSearchChange = (event: any) => {
    const searchQuery = event?.target?.value;
    debouncedFetchGroups(searchQuery || null);
  };

  return (
    <Box sx={{ textAlign: "left" }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{display: 'flex', justifyContent: "space-between", alignItems: 'center'}}>
              <Typography
                color="textPrimary"
                sx={{ textAlign: "left" }}
                variant="h6"
              >
              Select Group
              </Typography>
              <Typography
                color="textPrimary"
                sx={{ textAlign: "left" }}
                variant="h6"
              >
              All
              <Checkbox checked={isGlobal} onChange={(()=> setIsGlobal(!isGlobal))} />
              </Typography>
              </Box>
              <Autocomplete
                sx={{minWidth: '100%'}}
                size="small"
                getOptionLabel={(option: any) => option?.name ?? ''}
                options={groups}
                value={selectedGroup}
                onInputChange={handleGroupSearchChange}
                disableClearable
                disabled={isGlobal}
                forcePopupIcon={false}
                onChange={(e, value) => {
                  setSelectedGroup(value)
                }}
                clearOnBlur
                filterOptions={(options, params) => {
                  const filtered = filter(options, item =>
                    lowerCase(item.name).includes(lowerCase(params.inputValue))
                  );
                  return filtered;
                }}
                renderInput={params => (
                  <TextField label="Search Group" sx={{minWidth: '100%'}} helperText='Select Group for which the pattern is applied' variant="outlined" {...params} />
                )}
              />
        </Grid>
        <Grid item xs={12}>
          <Typography
            color="textPrimary"
            sx={{ m: 1, ml: 0, textAlign: "left" }}
            variant="h6"
          >
            Pattern Name
          </Typography>
          <TextField
            autoComplete="off"
            sx={{ textAlign: "left" }}
            size="medium"
            fullWidth
            name="Name"
            placeholder="New Pattern"
            defaultValue={formValues?.name}
            onChange={(e: any) => {
              handleChange(e.target.value, "name");
            }}
            required
            variant="outlined"
          />
        </Grid>


        <Box
          sx={{
            display: "flex",
            mt: 3,
            ml: 3
          }}
        >
          {successMessage && successMessage.trim() ? null : (
            <>
              {hideSave ? null :
                (<Button
                  color="primary"
                  size="medium"
                  variant="contained"
                  disabled={isSubmitting ||  (!isGlobal && !selectedGroup) ||
                    formValues?.name.trim() === ""}
                  onClick={handleSubmit}
                >
                  Save
                </Button>)
              }
            </>

          )}
        </Box>
      </Grid>
    </Box>
  );
};

export default withRouter(AddVariableForm);  